/**
 * Brand Logo Library — upload multiple logo versions per client with usage
 * notes (consumed by AI Studio) and a validity window. A logo with an end
 * date in the past is retired and won't be used in new creative.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Images, Loader2, Save, Trash2, Upload, X } from "lucide-react";

function statusOf(l) {
  const today = new Date().toISOString().slice(0, 10);
  if (l.end_date && l.end_date < today) return { label: "Retired", cls: "bg-red-50 text-red-700 border-red-200" };
  if (l.start_date && l.start_date > today) return { label: "Scheduled", cls: "bg-amber-50 text-amber-700 border-amber-200" };
  return { label: "Current", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
}

export default function BrandLogoLibrary({ clientId }) {
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${clientId}/brand-logos`);
      setLogos(data?.logos || []);
    } catch { setLogos([]); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const upload = async (fileList) => {
    setUploading(true);
    try {
      for (const f of Array.from(fileList)) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/clients/${clientId}/brand-logos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Logo uploaded — add usage notes so the AI knows where to use it");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const patch = async (logoId, payload, okMsg) => {
    try {
      await api.patch(`/clients/${clientId}/brand-logos/${logoId}`, payload);
      if (okMsg) toast.success(okMsg);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete "${l.original_filename}" from the logo library?`)) return;
    try {
      await api.delete(`/clients/${clientId}/brand-logos/${l.logo_id}`);
      toast.success("Logo removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Card className="rounded-sm" data-testid="brand-logo-library">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="label-overline flex items-center gap-1.5">
              <Images className="h-3.5 w-3.5" /> Brand logo library
            </div>
            <h3 className="font-display font-bold text-lg tracking-tight mt-1">
              Logo versions & usage notes
            </h3>
            <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
              Upload every variant (primary, reversed, mono, seasonal…). The notes
              tell the team <strong>and the AI Studio</strong> where each version
              belongs. Leave the end date empty while a logo is current — set an
              end date to retire it, and it won&apos;t be used in creative after
              that day.
            </p>
          </div>
          <label className="inline-flex items-center gap-1.5 h-10 px-4 rounded-sm cursor-pointer
            bg-zinc-900 hover:bg-black text-white text-sm font-medium transition-colors">
            <input ref={fileRef} type="file" multiple className="hidden"
              accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
              onChange={(e) => e.target.files?.length && upload(e.target.files)}
              data-testid="brand-logo-file-input" />
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? "Uploading…" : "Upload logo"}
          </label>
        </div>

        {loading ? (
          <div className="py-8 text-center text-sm text-zinc-500 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading logo library…
          </div>
        ) : logos.length === 0 ? (
          <div className="border border-dashed border-zinc-300 rounded-sm p-8 text-center text-sm text-zinc-500"
            data-testid="brand-logo-empty">
            No logos in the library yet — upload the client&apos;s logo versions above.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {logos.map((l) => (
              <LogoCard key={l.logo_id} l={l} onPatch={patch} onRemove={remove} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function LogoCard({ l, onPatch, onRemove }) {
  const [notes, setNotes] = useState(l.notes || "");
  const [startDate, setStartDate] = useState(l.start_date || "");
  const [endDate, setEndDate] = useState(l.end_date || "");
  const [saving, setSaving] = useState(false);
  const st = statusOf(l);
  const dirty = notes !== (l.notes || "")
    || startDate !== (l.start_date || "")
    || endDate !== (l.end_date || "");

  const save = async () => {
    setSaving(true);
    await onPatch(l.logo_id, {
      notes,
      start_date: startDate || null,
      end_date: endDate || null,
      clear_end_date: !endDate,
    }, "Logo details saved");
    setSaving(false);
  };

  return (
    <div className="border border-zinc-200 rounded-sm p-3 flex gap-3 bg-white"
      data-testid={`brand-logo-card-${l.logo_id}`}>
      <div className="shrink-0 flex flex-col items-center gap-1.5">
        <div className="h-24 w-24 rounded-sm border border-zinc-200 grid place-items-center
          bg-[repeating-conic-gradient(#f4f4f5_0%_25%,white_0%_50%)] bg-[length:16px_16px] overflow-hidden">
          <img src={`${API}${l.url.replace(/^\/api/, "")}`}
            alt={l.original_filename}
            className="max-h-20 max-w-20 object-contain" />
        </div>
        <Badge variant="outline" className={`rounded-sm text-[10px] ${st.cls}`}
          data-testid={`brand-logo-status-${l.logo_id}`}>
          {st.label}
        </Badge>
      </div>
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-xs font-display font-bold truncate">{l.original_filename}</div>
            <div className="text-[10px] font-mono text-zinc-500">
              uploaded {new Date(l.uploaded_at).toLocaleDateString("en-AU")} ·{" "}
              {Math.round((l.size_bytes || 0) / 1024)} KB
            </div>
          </div>
          <Button size="sm" variant="ghost"
            className="rounded-sm h-7 px-2 text-red-600 hover:bg-red-50 shrink-0"
            onClick={() => onRemove(l)}
            data-testid={`brand-logo-delete-${l.logo_id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div>
          <Label className="label-overline text-[9px]">Usage notes for the team & AI</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="e.g. Reversed white version — only on dark backgrounds and video outros"
            className="mt-0.5 rounded-sm text-xs"
            data-testid={`brand-logo-notes-${l.logo_id}`} />
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <Label className="label-overline text-[9px]">Use from</Label>
            <Input type="date" value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="mt-0.5 rounded-sm h-8 text-xs font-mono w-[140px]"
              data-testid={`brand-logo-start-${l.logo_id}`} />
          </div>
          <div>
            <Label className="label-overline text-[9px]">Use until (empty = current)</Label>
            <div className="mt-0.5 flex items-center gap-1">
              <Input type="date" value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="rounded-sm h-8 text-xs font-mono w-[140px]"
                data-testid={`brand-logo-end-${l.logo_id}`} />
              {endDate && (
                <button type="button" title="Clear end date — mark as current"
                  onClick={() => setEndDate("")}
                  className="h-8 w-8 grid place-items-center rounded-sm border border-zinc-200
                    text-zinc-500 hover:text-red-600 hover:border-red-200"
                  data-testid={`brand-logo-clear-end-${l.logo_id}`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
          <Button size="sm" onClick={save} disabled={!dirty || saving}
            className="rounded-sm h-8 bg-[#002FA7] hover:bg-[#001f70] text-white ml-auto"
            data-testid={`brand-logo-save-${l.logo_id}`}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
